/**
 * DetailPopover
 *
 * Reusable popover for inline details. Opens on click of the trigger element,
 * closes on click outside or Escape. Supports placement (top/bottom/left/right)
 * with an arrow pointing to the trigger.
 *
 * @module components/ui/DetailPopover
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

const ARROW_SIZE = 8;

const PLACEMENT_STYLES = {
  top: {
    panel: 'bottom-full left-1/2 -translate-x-1/2 mb-3',
    arrow: 'top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-white/20',
  },
  bottom: {
    panel: 'top-full left-1/2 -translate-x-1/2 mt-3',
    arrow: 'bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-white/20',
  },
  left: {
    panel: 'right-full top-1/2 -translate-y-1/2 mr-3',
    arrow: 'left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-white/20',
  },
  right: {
    panel: 'left-full top-1/2 -translate-y-1/2 ml-3',
    arrow: 'right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-white/20',
  },
};

export default function DetailPopover({ trigger, children, placement = 'bottom' }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    // Use a small delay so the opening click doesn't immediately close
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const placementStyle = PLACEMENT_STYLES[placement] || PLACEMENT_STYLES.bottom;

  return (
    <div ref={containerRef} className="relative inline-block">
      {/* Trigger */}
      <div
        onClick={toggle}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-haspopup="true"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        className="cursor-pointer"
      >
        {trigger}
      </div>

      {/* Popover */}
      {isOpen && (
        <div
          className={`absolute z-[1300] ${placementStyle.panel}`}
          role="dialog"
          aria-modal="false"
        >
          {/* Arrow */}
          <div
            className={`absolute w-0 h-0 border-solid ${placementStyle.arrow}`}
            style={{ borderWidth: ARROW_SIZE }}
          />

          {/* Content */}
          <div className="bg-black/80 backdrop-blur-xl border border-white/10 rounded-xl shadow-glass-lg max-h-80 overflow-y-auto min-w-[240px] max-w-[360px]">
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

DetailPopover.propTypes = {
  /** Element that triggers the popover on click */
  trigger: PropTypes.node.isRequired,
  /** Popover content */
  children: PropTypes.node.isRequired,
  /** Placement relative to the trigger */
  placement: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
};
